import {
  AfriexTransactionChannel,
  PaymentMethodNameResolutionStatus,
} from '@afriex/core/domain/enums';

import {TableSearchInput} from '@/components';
import {Select} from '@/components/common/Input';
import {useCountryList} from '@/context/CountryListContext';
import {mapCountryCodeToFlags} from '@/utils/countries';

import type {NameResolutionFilterState} from './types';
import {ANY} from './types';

type NameResolutionFiltersProps = {
  filters: NameResolutionFilterState;
  resolverOptions: string[];
  setFilter: <K extends keyof NameResolutionFilterState>(
    key: K,
  ) => (value: NameResolutionFilterState[K]) => void;
};

const NameResolutionFilters = ({
  filters,
  resolverOptions,
  setFilter,
}: NameResolutionFiltersProps) => {
  const {
    state: {countryList},
  } = useCountryList();

  const flags = mapCountryCodeToFlags(countryList ?? []);
  const countryOptions = (countryList ?? []).map((country) => ({
    key: `${flags[country.iso2] ?? ''} ${country.name} (${country.iso2})`.trim(),
    value: country.iso2,
  }));
  const isMobile = filters.channel === AfriexTransactionChannel.MOBILE_MONEY;

  return (
    <div className="mt-5 w-full grid grid-cols-1 md:grid-cols-[repeat(4,minmax(0,1fr))_minmax(0,2fr)] gap-3 items-center">
      <Select
        name="channel"
        aria-label="Channel"
        value={filters.channel}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setFilter('channel')(
            e.target.value as NameResolutionFilterState['channel'],
          )
        }
        options={[
          {key: 'Bank and mobile money', value: ANY},
          {key: 'Bank', value: AfriexTransactionChannel.BANK_ACCOUNT},
          {key: 'Mobile money', value: AfriexTransactionChannel.MOBILE_MONEY},
        ]}
      />
      <Select
        name="country"
        aria-label="Country"
        value={filters.country}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setFilter('country')(e.target.value)
        }
        options={[{key: 'Any country', value: ANY}, ...countryOptions]}
      />
      <Select
        name="resolvedBy"
        aria-label="Resolved by"
        value={filters.resolvedBy}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setFilter('resolvedBy')(e.target.value)
        }
        options={[
          {key: 'Any resolver', value: ANY},
          ...resolverOptions.map((resolver) => ({
            key: resolver,
            value: resolver,
          })),
        ]}
      />
      <Select
        name="status"
        aria-label="Status"
        value={filters.status}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setFilter('status')(
            e.target.value as NameResolutionFilterState['status'],
          )
        }
        options={[
          {key: 'Active', value: PaymentMethodNameResolutionStatus.ACTIVE},
          {
            key: 'Invalidated',
            value: PaymentMethodNameResolutionStatus.EXPIRED,
          },
          {key: 'Any status', value: ANY},
        ]}
      />
      <TableSearchInput
        value={filters.search}
        onChange={setFilter('search')}
        placeholder={
          isMobile
            ? 'Phone number (local or +233...), with a country'
            : 'Exact account number, or phone as +233...'
        }
      />
    </div>
  );
};

export default NameResolutionFilters;
